// @ts-nocheck
// app/course/[id].tsx — Course details page (SEO + Course Schema)
import Head from 'expo-router/head';
import { useEffect, useMemo, useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  ActivityIndicator,
  Alert,
  Linking,
} from 'react-native';
import { Redirect, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { ExternalLink, ArrowRight, MessageCircle, List } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';

const isWeb = Platform.OS === 'web';

function safeStr(v: any) {
  return (v ?? '').toString().trim();
}

function getFirstCsv(raw?: string | null) {
  const s = safeStr(raw);
  if (!s) return '';
  return s.split(',').map((p) => p.trim()).filter(Boolean)[0] || '';
}

function normalizeUrl(raw?: string | null) {
  const s = safeStr(raw);
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[\w-]+\.[\w.-]+/i.test(s)) return `https://${s}`;
  return s;
}

function extractPriceNumber(raw?: string) {
  // תופס מספרים כמו 3,990 / 3990 / 399
  const s = safeStr(raw);
  if (!s) return null;
  const m = s.replace(/[^\d.,]/g, '').match(/(\d[\d.,]*)/);
  if (!m) return null;
  const normalized = m[1].replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

type CourseRow = {
  id: number;
  course_id: string;
  source_url_key: string;
  title: string;
  provider: string;
  topic: string;
  type: string;
  level: string;
  format: string;
  price: string;
  duration: string;
  language: string;
  summary: string;
  source_urls: string;
  voucher_text: string;
  has_voucher: boolean;
  audience: string;
  outcomes: string;
};

export default function CoursePage() {
  const router = useRouter();
  const params = useLocalSearchParams();

  const courseKey = safeStr(params.id);

  const [course, setCourse] = useState<CourseRow | null>(null);
  const [loading, setLoading] = useState(true);

  const canonicalUrl = useMemo(() => {
    const key = encodeURIComponent(course?.source_url_key || course?.course_id || courseKey);
    return `https://kursiaii.com/course/${key}`;
  }, [course, courseKey]);

  if (isWeb && courseKey) {
    return <Redirect href={`/course/${encodeURIComponent(courseKey)}`} />;
  }

  useEffect(() => {
    if (!courseKey) return;
    if (!isWeb) return;
    router.replace(`/course/${encodeURIComponent(courseKey)}`);
  }, [courseKey, router]);

  useEffect(() => {
    let alive = true;

    const fetchCourse = async () => {
      try {
        setLoading(true);

        const key = safeStr(courseKey);
        if (!key) {
          if (alive) setCourse(null);
          return;
        }

        const { data, error } = await supabase
          .from('processed_raw_courses')
          .select(
            'id,course_id,source_url_key,title,provider,topic,type,level,format,price,duration,language,summary,source_urls,voucher_text,has_voucher,audience,outcomes'
          )
          .is('deleted_at', null)
          .or(`source_url_key.eq.${key},course_id.eq.${key}`)
          .order('id', { ascending: false })
          .limit(1);

        if (!alive) return;

        if (error || !data || data.length === 0) {
          setCourse(null);
          return;
        }

        // normalize strings lightly
        const c = data[0] as any;
        const normalized: CourseRow = {
          ...c,
          course_id: safeStr(c.course_id),
          source_url_key: safeStr(c.source_url_key),
          title: safeStr(c.title),
          provider: safeStr(c.provider),
          topic: safeStr(c.topic),
          type: safeStr(c.type),
          level: safeStr(c.level),
          format: safeStr(c.format),
          price: safeStr(c.price),
          duration: safeStr(c.duration),
          language: safeStr(c.language),
          summary: safeStr(c.summary),
          source_urls: safeStr(c.source_urls),
          voucher_text: safeStr(c.voucher_text),
          audience: safeStr(c.audience),
          outcomes: safeStr(c.outcomes),
          has_voucher: !!c.has_voucher,
        };

        setCourse(normalized);
      } catch (e) {
        if (alive) setCourse(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchCourse();
    return () => {
      alive = false;
    };
  }, [courseKey]);

  const openExternal = useCallback(async () => {
    const raw = course ? getFirstCsv(course.source_urls) : '';
    const url = normalizeUrl(raw);
    if (!url) {
      if (!isWeb) Alert.alert('אין קישור', 'לקורס הזה אין לינק פתיחה תקין כרגע.');
      return;
    }

    if (isWeb && typeof window !== 'undefined') {
      (window as any).open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const ok = await Linking.canOpenURL(url);
      if (!ok) {
        Alert.alert('לא ניתן לפתוח קישור', 'הלינק לקורס לא תקין או חסום במכשיר הזה.');
        return;
      }
      await Linking.openURL(url);
    } catch {
      Alert.alert('שגיאה', 'נכשלנו בפתיחת הקישור. נסה שוב.');
    }
  }, [course]);

  const goToChat = useCallback(() => {
    const seed = course?.title
      ? `אני רוצה התאמה לקורסים דומים ל-${course.title}. חשוב לי שתשאל שאלה אחת אם חסר פרט.`
      : 'אני רוצה התאמה אישית לקורס';

    router.push({
      pathname: '/chat',
      params: { initialMessage: seed, skipWelcome: 'true', source: 'course_page' as any },
    });
  }, [router, course]);

  const goToCatalog = useCallback(() => router.push('/catalog'), [router]);

  const jsonLd = useMemo(() => {
    if (!course) return null;

    const firstUrl = normalizeUrl(getFirstCsv(course.source_urls));
    const priceNum = extractPriceNumber(course.price);

    // Course schema בסיסי (לא לחרטט נתונים שלא קיימים)
    const obj: any = {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title || 'קורס',
      description: course.summary || course.outcomes || course.audience || 'קורס בישראל',
      provider: course.provider
        ? { '@type': 'Organization', name: course.provider }
        : { '@type': 'Organization', name: 'Kursi' },
      inLanguage: course.language || 'he',
    };

    if (course.topic) obj.about = course.topic;
    if (course.level) obj.educationalLevel = course.level;
    if (course.format) obj.courseMode = course.format;
    if (course.duration) obj.timeRequired = course.duration;

    if (firstUrl) obj.url = canonicalUrl;

    if (priceNum !== null) {
      obj.offers = {
        '@type': 'Offer',
        price: priceNum,
        priceCurrency: 'ILS',
        availability: 'https://schema.org/InStock',
        url: firstUrl || canonicalUrl,
      };
    }

    return JSON.stringify(obj);
  }, [course, canonicalUrl]);

  const pageTitle = course?.title ? `${course.title} | Kursi` : 'קורס | Kursi';
  const pageDesc =
    course?.summary ||
    (course?.title ? `פרטים על הקורס ${course.title} — מחיר, משך, רמה וספק.` : 'פרטי קורס ב-Kursi.');

  return (
    <View style={styles.page}>
      <Stack.Screen options={{ title: course?.title || 'קורס' }} />

      {isWeb && (
        <Head>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDesc} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDesc} />
          <meta name="robots" content="noindex,follow" />
          {!!jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />}
        </Head>
      )}

      <ScrollView contentContainerStyle={styles.wrap} showsVerticalScrollIndicator={false}>
        <View style={styles.topBar}>
          <Pressable onPress={() => router.push('/')} style={({ pressed }) => [styles.linkBtn, pressed && styles.pressed]}>
            <ArrowRight size={18} color={theme.colors.text} />
            <Text style={styles.linkText}>דף הבית</Text>
          </Pressable>

          <Pressable onPress={goToCatalog} style={({ pressed }) => [styles.linkBtn, pressed && styles.pressed]}>
            <List size={18} color={theme.colors.text} />
            <Text style={styles.linkText}>קטלוג</Text>
          </Pressable>
        </View>

        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.muted}>טוען קורס…</Text>
          </View>
        ) : !course ? (
          <View style={styles.card}>
            <Text style={styles.h1}>הקורס לא נמצא</Text>
            <Text style={styles.muted}>יכול להיות שהלינק ישן או שהקורס הוסר.</Text>

            <Pressable onPress={goToCatalog} style={({ pressed }) => [styles.primaryBtn, pressed && styles.pressed]}>
              <Text style={styles.primaryBtnText}>חזרה לקטלוג</Text>
            </Pressable>
          </View>
        ) : (
          <>
            <View style={styles.card}>
              {course.has_voucher && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{course.voucher_text || 'הטבה'}</Text>
                </View>
              )}

              <Text style={styles.h1}>{course.title || 'קורס'}</Text>

              {!!course.provider && <Text style={styles.provider}>{course.provider}</Text>}

              <View style={styles.metaRow}>
                {!!course.topic && <Pill text={course.topic} />}
                {!!course.type && <Pill text={course.type} />}
                {!!course.level && <Pill text={course.level} />}
                {!!course.format && <Pill text={course.format} />}
                {!!course.duration && <Pill text={`⏱ ${course.duration}`} />}
                {!!course.price && <Pill text={`₪ ${course.price}`} />}
              </View>

              {!!course.summary && <Text style={styles.paragraph}>{course.summary}</Text>}
              {!course.summary && !!course.outcomes && <Text style={styles.paragraph}>{course.outcomes}</Text>}
              {!course.summary && !course.outcomes && !!course.audience && <Text style={styles.paragraph}>{course.audience}</Text>}

              <View style={styles.actions}>
                <Pressable onPress={openExternal} style={({ pressed }) => [styles.primaryBtn, pressed && styles.pressed]}>
                  <ExternalLink size={16} color="#fff" />
                  <Text style={styles.primaryBtnText}>לאתר הקורס</Text>
                </Pressable>

                <Pressable onPress={goToChat} style={({ pressed }) => [styles.outlineBtn, pressed && styles.pressed]}>
                  <MessageCircle size={16} color={theme.colors.primary} />
                  <Text style={styles.outlineBtnText}>התאמה לקורסים דומים</Text>
                </Pressable>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.h2}>מה קורה עכשיו?</Text>
              <Text style={styles.muted}>
                1) אתה על דף קורס פנימי של Kursi (טוב ל-SEO){"\n"}
                2) לחיצה על “לאתר הקורס” פותחת את הספק החיצוני{"\n"}
                3) אפשר לעשות התאמה בצ׳אט לקורסים דומים
              </Text>
            </View>
          </>
        )}

        <View style={{ height: 30 }} />
      </ScrollView>
    </View>
  );
}

function Pill({ text }: { text: string }) {
  const t = safeStr(text);
  if (!t) return null;
  return (
    <View style={styles.pill}>
      <Text style={styles.pillText} numberOfLines={1}>
        {t}
      </Text>
    </View>
  );
}

const _PAPER = '#F2EBDD';
const _PAPER_SOFT = '#FAF5EA';
const _INK = '#1A1611';
const _INK_SOFT = '#3A332A';
const _INK_MUTED = '#7A6F5F';
const _ACCENT = '#C8531E';
const SERIF_BLACK = 'FrankRuhlLibre-Black';
const SERIF_BOLD = 'FrankRuhlLibre-Bold';
const SANS = 'Heebo-Regular';
const SANS_BOLD = 'Heebo-Bold';
const MONO = 'SpaceMono-Regular';

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: _PAPER },
  wrap: {
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 48,
    width: '100%',
    maxWidth: isWeb ? 1080 : undefined,
    alignSelf: 'center',
  },

  topBar: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: _INK,
  },

  linkBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: _INK,
    backgroundColor: 'transparent',
  } as any,
  linkText: { fontFamily: MONO, fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase' as any, color: _INK },

  center: { paddingVertical: 80, alignItems: 'center', gap: 16 },

  card: {
    borderWidth: 1,
    borderColor: _INK,
    backgroundColor: _PAPER_SOFT,
    padding: 32,
    marginBottom: 16,
  },

  badge: {
    alignSelf: 'flex-end',
    borderWidth: 2,
    borderColor: _ACCENT,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginBottom: 18,
    transform: [{ rotate: '-2deg' }],
  } as any,
  badgeText: { color: _ACCENT, fontFamily: MONO, fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right' },

  h1: {
    fontFamily: SERIF_BLACK,
    fontSize: isWeb ? 56 : 34,
    color: _INK,
    textAlign: 'right',
    lineHeight: isWeb ? 60 : 38,
    letterSpacing: -1.4,
  },
  provider: {
    marginTop: 10,
    fontFamily: MONO, fontSize: 12, color: _INK_MUTED,
    letterSpacing: 1.4, textTransform: 'uppercase' as any,
    textAlign: 'right',
  },

  metaRow: {
    marginTop: 20, paddingTop: 18,
    borderTopWidth: 1, borderTopColor: _INK,
    flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8,
  } as any,

  pill: {
    borderWidth: 1, borderColor: _INK,
    paddingVertical: 5, paddingHorizontal: 12,
    backgroundColor: 'transparent',
  },
  pillText: { fontFamily: MONO, fontSize: 11, color: _INK, letterSpacing: 1.2, textTransform: 'uppercase' as any, textAlign: 'right' },

  paragraph: {
    marginTop: 18,
    fontFamily: SANS, fontSize: 16,
    color: _INK_SOFT, textAlign: 'right', lineHeight: 27,
  },

  h2: {
    fontFamily: SERIF_BOLD, fontSize: 26,
    color: _INK, textAlign: 'right',
    marginBottom: 14, letterSpacing: -0.4,
  },

  muted: {
    fontFamily: SANS, fontSize: 14,
    color: _INK_MUTED, textAlign: 'right', lineHeight: 22,
  },

  actions: { marginTop: 22, gap: 10, flexDirection: 'row-reverse', flexWrap: 'wrap' } as any,

  primaryBtn: {
    flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 10,
    backgroundColor: _INK, borderWidth: 1, borderColor: _INK,
    paddingVertical: 14, paddingHorizontal: 22,
  } as any,
  primaryBtnText: { fontFamily: SANS_BOLD, color: _PAPER, fontSize: 14, textAlign: 'center', letterSpacing: 0.3 },

  outlineBtn: {
    flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 10,
    borderWidth: 1, borderColor: _INK,
    paddingVertical: 14, paddingHorizontal: 22,
    backgroundColor: 'transparent',
  } as any,
  outlineBtnText: { fontFamily: SANS_BOLD, color: _INK, fontSize: 14, textAlign: 'center', letterSpacing: 0.3 },

  pressed: { opacity: 0.92 },
});
